import type { ReactElement } from "react";
// Explicit React import: tsx --test compiles JSX with the classic runtime.
import React from "react";
import { cn } from "@ndd/ui";
import { GoalBar } from "./goal-bar";
import { THEMED_EYEBROW } from "../../lib/always-look";
import {
  EMPTY_GOAL_BAR,
  EM_DASH,
  NO_DATA_SUB,
  cellValue,
  goalBarModel,
  type RosterMetricName,
} from "../../lib/roster-lanes";
import type { CompactMetricEnvelope } from "../../../../shared/metrics";

/*
 * The two things a roster row can show in the metric columns. They live here,
 * apart from `roster-lanes.tsx`, so they load under `tsx --test` with relative
 * imports only and the difference between them can be asserted on rendered
 * markup — that difference is what NO-618 is about.
 *
 * Both keep the same shape — value line, sub-label, goal bar — so an uncovered
 * RD reads as a row of the roster rather than an interruption in it. A
 * published cell shows its value, or an em dash plus its reason when the
 * envelope is Too early or null. An uncovered cell shows an em dash, the
 * sub-label "No Data", and the bare track, because there is no envelope behind
 * it to draw zones from. The sub-label is what tells them apart.
 */

export const COLUMN_LABEL = cn(
  "text-caption tracking-wider text-muted-foreground font-medium uppercase",
  THEMED_EYEBROW
);

export function MetricCell({
  name,
  label,
  envelope,
}: {
  name: RosterMetricName;
  label: string;
  envelope: CompactMetricEnvelope;
}): ReactElement {
  const cell = cellValue(name, envelope);

  return (
    <div className="min-w-0" data-roster-cell="published">
      <p className={cn("md:hidden text-micro", COLUMN_LABEL)}>{label}</p>
      <div className="flex items-baseline gap-1.5 min-w-0">
        <span className="text-sm font-bold text-foreground">{cell.display}</span>
        {cell.sub !== null && (
          <span className="text-micro text-muted-foreground truncate">{cell.sub}</span>
        )}
      </div>
      <div className="mt-1.5">
        <GoalBar model={goalBarModel(name, envelope)} />
      </div>
    </div>
  );
}

export function NotPublishedCell({ label }: { label: string }): ReactElement {
  return (
    <div className="min-w-0" data-roster-cell="not-published">
      <p className={cn("md:hidden text-micro", COLUMN_LABEL)}>{label}</p>
      <div className="flex items-baseline gap-1.5 min-w-0">
        <span className="text-sm font-bold text-foreground">{EM_DASH}</span>
        <span className="text-micro text-muted-foreground truncate">{NO_DATA_SUB}</span>
      </div>
      <div className="mt-1.5">
        <GoalBar model={EMPTY_GOAL_BAR} />
      </div>
    </div>
  );
}
